import { FC, useEffect, useRef, useState, useSyncExternalStore } from "react";
import ghostError from "../../assets/images/ghost-error.gif";
import ghostErrorStill from "../../assets/images/ghost-error.png";
import ghostIdle from "../../assets/images/ghost-idle.gif";
import ghostLoader from "../../assets/images/ghost-loader.gif";
import ghostSuccess from "../../assets/images/ghost-success.gif";
import ghostSuccessStill from "../../assets/images/ghost-success.png";
import ghostSwirl from "../../assets/images/ghost-swirl.gif";
import ghostStill from "../../assets/images/ghost.png";
import ghostWink from "../../assets/images/ghost.gif";

/**
 * still: the logo pose. idle: blinks now and then (6 s loop).
 * wink: winks and turns once around (2 s loop).
 * loader: turns into a pill whose eyes run like a loading bar (2.5 s loop).
 * swirl: swirls into a ring spinner (2.5 s loop).
 * success / error: play once (1.5 s) and stay on the happy / × face.
 */
export type PrivacyGhostMotion =
  | "still"
  | "idle"
  | "wink"
  | "loader"
  | "swirl"
  | "success"
  | "error";

/** One pass of the wink animation. */
export const PRIVACY_GHOST_WINK_MS = 2_000;

// The still of success and error is their final face, so reduced motion
// still says how it went.
const SOURCES: Record<PrivacyGhostMotion, { moving: string; still: string }> = {
  still: { moving: ghostStill, still: ghostStill },
  idle: { moving: ghostIdle, still: ghostStill },
  wink: { moving: ghostWink, still: ghostStill },
  loader: { moving: ghostLoader, still: ghostStill },
  swirl: { moving: ghostSwirl, still: ghostStill },
  success: { moving: ghostSuccess, still: ghostSuccessStill },
  error: { moving: ghostError, still: ghostErrorStill },
};

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_MOTION);
  query.addEventListener?.("change", onChange);
  return () => query.removeEventListener?.("change", onChange);
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  !!window.matchMedia &&
  window.matchMedia(REDUCED_MOTION).matches;

/**
 * The header ghost: still while private mode is off, idle while it is on.
 * It winks on hover and once when private mode turns on.
 */
export const useHeaderGhostMotion = (active: boolean) => {
  const [hover, setHover] = useState(false);
  const [winking, setWinking] = useState(false);
  const wasActive = useRef(active);
  useEffect(() => {
    const turnedOn = active && !wasActive.current;
    wasActive.current = active;
    if (!turnedOn) {
      if (!active) setWinking(false);
      return;
    }
    setWinking(true);
    const timer = setTimeout(() => setWinking(false), PRIVACY_GHOST_WINK_MS);
    return () => clearTimeout(timer);
  }, [active]);
  const motion: PrivacyGhostMotion =
    hover || (active && winking) ? "wink" : active ? "idle" : "still";
  return { motion, setHover };
};

/**
 * The Redacted ghost, drawn 1:1 from the logo. Like the other animated
 * icons it is black on transparent: colour it with a `filter-*` class.
 */
export const PrivacyGhost: FC<{
  motion?: PrivacyGhostMotion;
  className?: string;
  alt?: string;
}> = ({ motion = "still", className, alt = "" }) => {
  const reduced = useSyncExternalStore(
    subscribeReducedMotion,
    prefersReducedMotion,
    () => false
  );
  return (
    <img
      src={reduced ? SOURCES[motion].still : SOURCES[motion].moving}
      className={className}
      alt={alt}
      aria-hidden={alt ? undefined : true}
      data-motion={reduced ? "still" : motion}
    />
  );
};
